import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ExternalLink, Phone } from "lucide-react";
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

const REALTOR_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "description", label: "Description" },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "phone", label: "Phone Number", placeholder: "e.g. 972-333-4801" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

export interface RealtorEntry {
  name: string;
  description: string;
  website: string;
  address: string;
  image: string;
  phone?: string;
  hidePhoneNumber?: boolean;
  facebook?: string;
}

export const REALTOR_ENTRIES: RealtorEntry[] = [
  {
    name: "APARNA DUBAGUNTA",
    description: "",
    website: "",
    address: "100 N Central Expy, Suite 913 Richardson, TX 75080",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782577098872-opbk1u.jpg",
  },
  {
    name: "HASAN SOOMRO",
    description: "",
    website: "",
    address: "",
    phone: "972-333-4801",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783543996133-8ku584.png",
  },
  {
    name: "Jasmine Soni",
    description: "",
    website: "",
    address: "",
    phone: "408-499-2728",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784579268602-3x1is.jpg",
  },
  {
    name: "Lilu Sells Texas",
    description: "Frisco",
    website: "",
    address: "",
    facebook: "https://www.facebook.com/lilusellstexas",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-19/b6923a4e82a0.jpeg",
  },
  {
    name: "SUNDEEP KHETRAPAL",
    description: "",
    website: "",
    address: "100 N Central Expy, Suite 913 Richardson, TX 75080",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782577429141-4zlnt.jpg",
  },
  {
    name: "TANUJA MAHINDRU",
    description: "",
    website: "",
    address: "",
    phone: "818-205-5604",
    hidePhoneNumber: true,
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784499133924-k7gwng.png",
  },
  {
    name: "Prarthana Pandey Pokhrel",
    description: "",
    website: "",
    address: "P3 Real Estate Group, 500 Briarwood Dr, Southlake, TX 76092",
    phone: "817-401-4436",
    image: "",
  },
];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop";

function mapsUrl(e: RealtorEntry) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address}`)}`;
}

export function RealtorCard({ entry, index }: { entry: RealtorEntry; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full h-[180px] relative overflow-hidden bg-gray-100 flex items-center justify-center">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-contain"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="p-1.5 pb-0.5 flex-1 flex flex-col justify-between">
        <div>
          <h3 className="text-[10px] font-bold text-[#082f6d] leading-tight truncate">
            {entry.name}
          </h3>
          {entry.description && (
            <p className="text-[9px] text-gray-500 font-medium leading-snug mt-0.5">
              {entry.description}
            </p>
          )}
          {entry.address && (
            <div className="flex items-start gap-0.5 mt-0.5">
              <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
              <span className="text-[9px] text-gray-500 font-medium leading-snug truncate">
                {entry.address}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="px-1.5 pb-1.5 pt-0.5 flex flex-col gap-1">
        {entry.website && (
          <form action={entry.website} method="get" target="_top" className="w-full">
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-0.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer"
            >
              <ExternalLink className="w-2.5 h-2.5" /> Visit Website
            </button>
          </form>
        )}
        {/* website already has its own dedicated "Visit Website" button above,
            so exclude it here — otherwise a small duplicate website icon shows */}
        <SocialLinks item={{ ...entry, facebook: undefined, website: undefined }} px={20} />
        {entry.facebook && (
          <a
            href={entry.facebook}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center gap-1.5 w-full bg-[#1877F2] hover:bg-[#0f5fcc] text-white text-[11px] font-bold py-2 rounded-md transition-colors duration-200"
          >
            <img
              src="https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-19/b6923a4e82a0.jpeg"
              alt=""
              className="w-4 h-4 rounded-full object-cover border border-white/60"
            />
            Follow on Facebook
          </a>
        )}
        {entry.phone && (
          <a
            href={`tel:${entry.phone.replace(/[^0-9+]/g, "")}`}
            className="flex items-center justify-center gap-0.5 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200"
          >
            <Phone className="w-2.5 h-2.5" /> Call Now
          </a>
        )}
        {entry.address && (
          <a
            href={mapsUrl(entry)}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center justify-center gap-0.5 text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200 ${
              entry.name === "APARNA DUBAGUNTA" || entry.name === "SUNDEEP KHETRAPAL" || entry.name === "Prarthana Pandey Pokhrel"
                ? "bg-[#082f6d] hover:bg-[#062454] text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
            }`}
          >
            <ExternalLink className="w-2.5 h-2.5" /> View Location
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function Realtors() {
  const [query, setQuery] = useState("");
  const mc = useManagedCollection<any>("realtors", REALTOR_ENTRIES, (e, i) => slugify(e.name || String(i)));

  const filtered = mc.items.filter(
    (e) =>
      query.trim() === "" ||
      e.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Realtors"
        placeholder="Search realtors…"
        backgroundImage="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&h=400&fit=crop"
        query={query}
        setQuery={setQuery}
      />
      <NewsTicker />

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Realtors & Real Estate Professionals
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              Find trusted real estate agents in the Dallas-DFW area.
            </p>
            <AdminAddBar mc={mc} fields={REALTOR_FIELDS} label="Add Realtor" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                {filtered.map((e, i) => (
                  <AdminItem key={e._key} mc={mc} item={e} fields={REALTOR_FIELDS} title="Realtor">
                    <RealtorCard entry={e as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No realtors match your search.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
